import { useState, useEffect, useCallback } from "react";
import GameCard from "../components/GameCard";
import CardBasedMobilePicks from "../components/CardBasedMobilePicks";
import Dropdown from "../components/ui/Dropdown";
import { useIsMobile } from "../hooks/useIsMobile";
import { apiService } from "../utils/apiService";
import { useLoadingState } from "../hooks/useLoadingState";
import { getWeekBoundaries } from "../utils/dateUtils";

const MakePicks = () => {
  const [games, setGames] = useState([]);
  const [userPicks, setUserPicks] = useState({});
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [currentWeek, setCurrentWeek] = useState(null);
  const [weeks, setWeeks] = useState([]);
  const isMobile = useIsMobile();
  const { loading, withLoading } = useLoadingState(true);
  const { loading: submitting, withLoading: withSubmitting } =
    useLoadingState();

  const fetchCurrentWeek = useCallback(async () => {
    try {
      const response = await apiService.get("/config/current-week");
      return response.currentWeek;
    } catch (err) {
      console.error("Failed to fetch current week:", err);
      return 1; // Fallback to week 1
    }
  }, []);

  const fetchAvailableWeeks = useCallback(async () => {
    try {
      // Fetch available weeks from the backend
      const response = await apiService.get("/games/weeks");
      const availableWeeks = response.weeks || [];

      const weekOptions = availableWeeks.map((week) => ({
        value: week.toString(),
        label: `Week ${week}`,
      }));

      setWeeks(weekOptions);

      // Set current week to the actual current week if not set
      if (availableWeeks.length > 0 && currentWeek === null) {
        const actualCurrentWeek = await fetchCurrentWeek();
        setCurrentWeek(actualCurrentWeek);
      }
    } catch (err) {
      // Fallback to showing weeks 1-2 since we know they exist
      const fallbackWeeks = [
        { value: "1", label: "Week 1" },
        { value: "2", label: "Week 2" },
      ];
      setWeeks(fallbackWeeks);
      if (currentWeek === null) {
        const actualCurrentWeek = await fetchCurrentWeek();
        setCurrentWeek(actualCurrentWeek);
      }
    }
  }, [currentWeek, fetchCurrentWeek]);

  const fetchGamesAndPicks = useCallback(async () => {
    if (!currentWeek) return;

    try {
      await withLoading(async () => {
        // Fetch games for the week
        const gamesResponse = await apiService.get(
          `/games/week/${currentWeek}?includeLines=true`,
        );

        // Fetch user's picks for the week
        const picksResponse = await apiService.get(
          `/picks/week/${currentWeek}`,
        );

        setGames(gamesResponse.games);

        // Convert picks array to object with gameId as key
        const picksMap = {};
        const picksArray = picksResponse.picks || picksResponse;
        picksArray.forEach((pick) => {
          picksMap[pick.game.id] = pick;
        });

        setUserPicks(picksMap);
      });
    } catch (err) {
      setError("Failed to load games or picks");
    }
  }, [currentWeek, withLoading]);

  useEffect(() => {
    fetchAvailableWeeks();
  }, [fetchAvailableWeeks]);

  useEffect(() => {
    if (currentWeek) {
      fetchGamesAndPicks();
    }
  }, [currentWeek, fetchGamesAndPicks]);

  const handleMakePick = async (gameId, teamAbbr) => {
    try {
      await withSubmitting(async () => {
        setError("");
        setSuccess("");

        // Submit the pick with team abbreviation (backend expects selectedTeam, not selectedTeamId)
        const response = await apiService.post("/picks", {
          gameId,
          selectedTeam: teamAbbr,
        });

        // Update local state
        setUserPicks((prev) => ({
          ...prev,
          [gameId]: response,
        }));

        setSuccess("Pick saved successfully!");

        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(""), 3000);
      });
    } catch (err) {
      apiService.handleError(err, setError, "Failed to save pick");
    }
  };

  const handleSubmitAllPicks = async () => {
    try {
      await withSubmitting(async () => {
        setError("");

        // Get all games that don't have picks yet
        const gamesWithoutPicks = games.filter(
          (game) => !userPicks[game.id] && game.status === "scheduled",
        );

        if (gamesWithoutPicks.length === 0) {
          setSuccess("All picks have been submitted!");
          setTimeout(() => setSuccess(""), 3000);
          return;
        }

        // Create batch of picks (default to home team) - backend expects selectedTeam (abbreviation)
        const picks = gamesWithoutPicks.map((game) => ({
          gameId: game.id,
          selectedTeam: game.homeTeam.abbreviation,
        }));

        // Submit batch
        const response = await apiService.post("/picks/batch", { picks });

        // Update local state with new picks
        const newPicks = {};
        const picksArray = response.picks || response;
        picksArray.forEach((pick) => {
          newPicks[pick.game.id] = pick;
        });

        setUserPicks((prev) => ({
          ...prev,
          ...newPicks,
        }));

        setSuccess("All picks submitted successfully!");
        setTimeout(() => setSuccess(""), 3000);
      });
    } catch (err) {
      apiService.handleError(err, setError, "Failed to submit picks");
    }
  };

  // Filter games based on pick status
  const getFilteredGames = () => {
    const scheduledGames = games.filter(g => g.status === "scheduled");
    const hasUnpickedGames = scheduledGames.some(g => !userPicks[g.id]);
    
    // If there are unpicked games, only show future games (scheduled)
    // If all picks are made, show all games for editing
    return hasUnpickedGames ? scheduledGames : games;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Mobile view - card based
  if (isMobile) {
    return (
      <CardBasedMobilePicks
        games={getFilteredGames()}
        userPicks={userPicks}
        onMakePick={handleMakePick}
        currentWeek={currentWeek}
        onWeekChange={setCurrentWeek}
        weeks={weeks}
      />
    );
  }

  // Desktop view
  return (
    <>
      <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold">
              {(() => {
                if (!currentWeek) return "Make Your Picks";
                const now = new Date();
                const seasonYear = now.getFullYear();
                const { weekStart } = getWeekBoundaries(currentWeek, seasonYear);
                return weekStart < now ? "Review Your Picks" : "Make Your Picks";
              })()}
            </h1>
            <p className="text-gray-600">Week {currentWeek}</p>
          </div>
          <Dropdown
            label="Week"
            value={currentWeek?.toString() || ""}
            onChange={(value) => setCurrentWeek(parseInt(value))}
            options={weeks}
            className="w-full sm:w-36"
          />
        </div>
        <div className="flex flex-col sm:flex-row gap-3 mt-4 md:mt-0 sm:items-end">
          {/*<button*/}
          {/*  className={`inline-flex items-center justify-center font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-3.5 text-sm max-h-20 ${*/}
          {/*    submitting ||*/}
          {/*    games.every(*/}
          {/*      (game) => userPicks[game.id] || game.status !== "scheduled",*/}
          {/*    )*/}
          {/*      ? "bg-gray-400 text-white"*/}
          {/*      : "bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-500"*/}
          {/*  }`}*/}
          {/*  onClick={handleSubmitAllPicks}*/}
          {/*  disabled={*/}
          {/*    submitting ||*/}
          {/*    games.every(*/}
          {/*      (game) => userPicks[game.id] || game.status !== "scheduled",*/}
          {/*    )*/}
          {/*  }*/}
          {/*>*/}
          {/*  Auto-Pick Remaining*/}
          {/*</button>*/}
        </div>
      </div>

      {error && (
        <div
          className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4"
          role="alert"
        >
          {error}
        </div>
      )}
      {success && (
        <div
          className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4"
          role="alert"
        >
          {success}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {games.map((game) => (
          <GameCard
            key={game.id}
            game={game}
            onMakePick={handleMakePick}
            showPickControls={game.status === "scheduled"}
            userPick={userPicks[game.id]}
          />
        ))}
      </div>
    </>
  );
};

export default MakePicks;
